import { useState, useEffect, useRef } from 'react';
import { useMagneticButton } from '../../../hooks/useMagneticButton';

export default function Hero() {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [sliderPosition, setSliderPosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isActivated, setIsActivated] = useState(false);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const heroRef = useRef<HTMLElement>(null);
  const magneticButton = useMagneticButton({ strength: 0.3, tolerance: 120 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20
      });
      setCursorPosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const scrollToContact = () => {
    const element = document.getElementById('contato');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSliderStart = (e: React.MouseEvent | React.TouchEvent) => {
    setIsDragging(true);
    e.preventDefault();
  };

  const handleSliderMove = (e: MouseEvent | TouchEvent) => {
    if (!isDragging || isActivated) return;
    
    const container = document.getElementById('slider-container');
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const position = Math.max(0, Math.min(clientX - rect.left - 30, rect.width - 60));
    const percentage = (position / (rect.width - 60)) * 100;
    
    setSliderPosition(percentage);

    if (percentage >= 90) {
      setIsActivated(true);
      setIsDragging(false);
      setTimeout(() => {
        scrollToContact();
        setTimeout(() => {
          setSliderPosition(0);
          setIsActivated(false);
        }, 1000);
      }, 300);
    }
  };

  const handleSliderEnd = () => {
    if (!isActivated) {
      setSliderPosition(0);
    }
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleSliderMove);
      window.addEventListener('mouseup', handleSliderEnd);
      window.addEventListener('touchmove', handleSliderMove);
      window.addEventListener('touchend', handleSliderEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleSliderMove);
      window.removeEventListener('mouseup', handleSliderEnd);
      window.removeEventListener('touchmove', handleSliderMove);
      window.removeEventListener('touchend', handleSliderEnd);
    };
  }, [isDragging, isActivated]);

  return (
    <section 
      ref={heroRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Custom Cursor Effect */}
      {isHovering && (
        <>
          <div 
            className="fixed w-8 h-8 border-2 border-teal-400 rounded-full pointer-events-none z-50 transition-transform duration-200 ease-out"
            style={{
              left: `${cursorPosition.x}px`,
              top: `${cursorPosition.y}px`,
              transform: `translate(-50%, -50%) scale(${isDragging ? 1.5 : 1})`
            }}
          ></div>
          <div 
            className="fixed w-2 h-2 bg-teal-400 rounded-full pointer-events-none z-50"
            style={{
              left: `${cursorPosition.x}px`,
              top: `${cursorPosition.y}px`,
              transform: 'translate(-50%, -50%)'
            }}
          ></div>
        </>
      )}

      <div className="absolute inset-0">
        <img
          src="https://static.readdy.ai/image/2fb69e05e600ee45b7cfaa2e2ddcddf8/47d78c009c8aa7fba691a045ee93272f.png"
          alt="Campo agrícola"
          className="w-full h-full object-cover object-center transition-transform duration-300 ease-out"
          style={{
            transform: `translate(${mousePosition.x}px, ${mousePosition.y}px) scale(1.1)`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-teal-900/60 via-emerald-900/50 to-cyan-900/60"></div>
      </div>

      {/* Elementos flutuantes decorativos com interação */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute top-20 left-10 w-20 h-20 border-4 border-teal-400/30 rounded-lg animate-pulse transition-transform duration-500"
          style={{
            transform: `translate(${mousePosition.x * 0.5}px, ${mousePosition.y * 0.5}px) rotate(${mousePosition.x * 0.5}deg)`
          }}
        ></div>
        <div 
          className="absolute top-40 right-20 w-16 h-16 border-4 border-cyan-400/30 rounded-full animate-pulse transition-transform duration-500" 
          style={{ 
            animationDelay: '1s',
            transform: `translate(${-mousePosition.x * 0.3}px, ${mousePosition.y * 0.3}px)`
          }}
        ></div>
        <div 
          className="absolute bottom-40 left-1/4 w-24 h-24 border-4 border-emerald-400/30 rounded-lg animate-pulse transition-transform duration-500" 
          style={{ 
            animationDelay: '2s',
            transform: `translate(${mousePosition.x * 0.4}px, ${-mousePosition.y * 0.4}px) rotate(${-mousePosition.x * 0.3}deg)`
          }}
        ></div>
        
        {/* Partículas flutuantes interativas */}
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white/40 rounded-full transition-all duration-700"
            style={{
              left: `${10 + (i * 6)}%`,
              top: `${20 + (i * 4)}%`,
              transform: `translate(${mousePosition.x * (0.2 + i * 0.05)}px, ${mousePosition.y * (0.2 + i * 0.05)}px)`,
              animation: `float ${3 + (i % 3)}s ease-in-out infinite`,
              animationDelay: `${i * 0.2}s`
            }}
          ></div>
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
        <div className="inline-block mb-6 animate-fade-in group cursor-pointer">
          <span className="bg-gradient-to-r from-emerald-400 to-teal-400 text-white px-6 py-3 rounded-full text-sm font-bold shadow-2xl border-2 border-white/30 transition-all duration-300 group-hover:scale-110 group-hover:shadow-[0_0_30px_rgba(20,184,166,0.6)]">
            <i className="ri-plant-line mr-2 inline-block group-hover:rotate-12 transition-transform duration-300"></i>
            Tecnologia para o Agronegócio
          </span>
        </div>

        <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-2 leading-tight animate-fade-in transition-all duration-300 hover:scale-105">
          Controle de Ponto Facial
        </h1>
        <h2 className="text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight animate-fade-in transition-all duration-300 hover:scale-105">
          Que funciona no campo
        </h2>
        <p className="text-xl md:text-2xl lg:text-3xl text-white/95 mb-10 max-w-4xl mx-auto leading-relaxed animate-fade-in-delay transition-all duration-300 hover:text-white">
          Sem depender de internet, sem fraudes, sem dor de cabeça
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in-delay-2">
          {/* Botão Deslizante com efeitos aprimorados */}
          <div 
            id="slider-container"
            className="relative w-full max-w-xs sm:max-w-md h-12 sm:h-16 bg-white/20 backdrop-blur-sm rounded-full border-2 border-white/30 overflow-hidden cursor-pointer select-none transition-all duration-300 hover:border-white/50 hover:shadow-[0_0_30px_rgba(255,255,255,0.3)]"
          >
            {/* Fundo de progresso */}
            <div 
              className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-200"
              style={{ 
                width: `${sliderPosition}%`,
                opacity: sliderPosition > 0 ? 0.3 : 0
              }}
            ></div>

            {/* Ondas de energia */}
            {sliderPosition > 0 && (
              <>
                <div 
                  className="absolute inset-0 bg-gradient-to-r from-emerald-400/20 to-teal-400/20 animate-pulse"
                  style={{ width: `${sliderPosition}%` }}
                ></div>
                <div 
                  className="absolute left-0 top-0 bottom-0 w-1 bg-teal-300 shadow-[0_0_20px_rgba(20,184,166,0.8)]"
                  style={{ left: `${sliderPosition}%` }}
                ></div>
              </>
            )}

            {/* Texto */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none px-10 sm:px-12">
              <span className={`text-white font-semibold text-xs sm:text-base transition-all duration-300 text-center ${
                sliderPosition > 50 ? 'opacity-0' : 'opacity-100'
              }`}>
                {isActivated ? 'Ativado!' : 'Deslize para falar com especialista'}
              </span>
              <span className={`absolute text-white font-semibold text-xs sm:text-base transition-all duration-300 ${
                sliderPosition > 50 ? 'opacity-100' : 'opacity-0'
              }`}>
                Continue deslizando...
              </span>
            </div>

            {/* Botão deslizante com efeito magnético */}
            <div
              ref={magneticButton.ref as React.RefObject<HTMLDivElement>}
              className={`absolute top-1.5 sm:top-2 left-1.5 sm:left-2 w-9 h-9 sm:w-12 sm:h-12 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full shadow-2xl flex items-center justify-center cursor-grab active:cursor-grabbing transition-all duration-200 ${
                isActivated ? 'scale-110' : 'scale-100'
              }`}
              style={{ 
                left: `calc(0.375rem + ${sliderPosition}%)`,
                transform: `translate(${magneticButton.position.x}px, ${magneticButton.position.y}px) scale(${isDragging ? 1.1 : magneticButton.isHovering ? 1.15 : 1})`,
                boxShadow: isDragging || magneticButton.isHovering ? '0 0 30px rgba(20, 184, 166, 0.8)' : '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
              }}
              onMouseDown={handleSliderStart}
              onTouchStart={handleSliderStart}
            >
              <i className={`ri-arrow-right-line text-lg sm:text-2xl text-white transition-transform duration-300 ${
                isDragging ? 'translate-x-1' : ''
              }`}></i>
              
              {/* Círculos de energia ao redor do botão */}
              {(isDragging || magneticButton.isHovering) && (
                <>
                  <div className="absolute inset-0 border-2 border-white/50 rounded-full animate-ping"></div>
                  <div className="absolute inset-0 border-2 border-teal-300/50 rounded-full animate-ping" style={{ animationDelay: '0.2s' }}></div>
                </>
              )}
            </div>

            {/* Indicador de sucesso */}
            {isActivated && (
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center">
                <i className="ri-check-line text-2xl sm:text-3xl text-white animate-bounce"></i>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce cursor-pointer group">
        <i className="ri-arrow-down-line text-4xl text-white/70 group-hover:text-white transition-colors duration-300"></i>
      </div>
    </section>
  );
}